/**
 * 图表国际化辅助类
 * 用于处理ECharts图表配置的国际化翻译
 */
class ChartI18nHelper {
  /**
   * 构造函数
   * @param {Object} i18n - Vue I18n实例
   */
  constructor(i18n) {
    this.i18n = i18n;
  }

  /**
   * 获取翻译文本
   * @param {string} key - 翻译键
   * @param {Object} params - 翻译参数
   * @returns {string} 翻译后的文本
   */
  t(key, params = {}) {
    if (!this.i18n) {
      console.warn('ChartI18nHelper: i18n实例未初始化');
      return key;
    }
    return this.i18n.t(key, params);
  }

  /**
   * 获取当前语言
   * @returns {string} 当前语言代码
   */
  getCurrentLanguage() {
    return this.i18n ? this.i18n.locale : 'zh-CN';
  }

  /**
   * 判断是否为英文环境
   * @returns {boolean} 是否为英文
   */
  isEnglish() {
    return this.getCurrentLanguage() === 'en-US';
  }

  /**
   * 判断是否为中文环境
   * @returns {boolean} 是否为中文
   */
  isChinese() {
    return this.getCurrentLanguage() === 'zh-CN';
  }

  /**
   * 获取翻译后的图表配置
   * @param {Object} baseOption - 基础图表配置
   * @returns {Object} 翻译后的图表配置
   */
  getTranslatedChartOption(baseOption) {
    if (!baseOption) {
      console.warn('ChartI18nHelper: baseOption不能为空');
      return {};
    }

    // 深拷贝基础配置，避免修改原始配置
    const translatedOption = JSON.parse(JSON.stringify(baseOption));

    // 翻译图表标题
    if (translatedOption.title) {
      translatedOption.title = this._translateTitle(translatedOption.title);
    }

    // 翻译坐标轴
    if (translatedOption.xAxis) {
      translatedOption.xAxis = this._translateAxis(translatedOption.xAxis);
    }

    if (translatedOption.yAxis) {
      translatedOption.yAxis = this._translateAxis(translatedOption.yAxis);
    }

    // 翻译系列名称
    if (translatedOption.series && Array.isArray(translatedOption.series)) {
      translatedOption.series = translatedOption.series.map(serie => 
        this._translateSeries(serie)
      );
    }

    return translatedOption;
  }

  /**
   * 翻译图表标题
   * @param {Object|Array} title - 标题配置
   * @returns {Object|Array} 翻译后的标题配置
   * @private
   */
  _translateTitle(title) {
    if (Array.isArray(title)) {
      return title.map(t => this._translateSingleTitle(t));
    }
    return this._translateSingleTitle(title);
  }

  /**
   * 翻译单个标题
   * @param {Object} title - 单个标题配置
   * @returns {Object} 翻译后的标题配置
   * @private
   */
  _translateSingleTitle(title) {
    const translatedTitle = { ...title };
    
    // 如果有翻译键，进行翻译
    if (title.textKey) {
      translatedTitle.text = this.t(title.textKey);
    }
    
    if (title.subtextKey) {
      translatedTitle.subtext = this.t(title.subtextKey);
    }

    return translatedTitle;
  }

  /**
   * 翻译坐标轴配置
   * @param {Object|Array} axis - 坐标轴配置
   * @param {string} axisType - 坐标轴类型 ('xAxis' 或 'yAxis')
   * @returns {Object|Array} 翻译后的坐标轴配置
   * @private
   */
  _translateAxis(axis) {
    if (Array.isArray(axis)) {
      return axis.map(a => this._translateSingleAxis(a));
    }
    return this._translateSingleAxis(axis);
  }

  /**
   * 翻译单个坐标轴
   * @param {Object} axis - 单个坐标轴配置
   * @returns {Object} 翻译后的坐标轴配置
   * @private
   */
  _translateSingleAxis(axis) {
    const translatedAxis = { ...axis };

    // 翻译坐标轴名称
    if (axis.nameKey) {
      translatedAxis.name = this.t(axis.nameKey);
    }

    // 翻译坐标轴数据标签
    if (axis.data && Array.isArray(axis.data)) {
      translatedAxis.data = axis.data.map(item => {
        if (typeof item === 'object' && item.labelKey) {
          return {
            ...item,
            value: this.t(item.labelKey)
          };
        } else if (typeof item === 'string' && item.startsWith('charts.')) {
          // 如果数据项本身就是翻译键
          return this.t(item);
        }
        return item;
      });
    }

    return translatedAxis;
  }

  /**
   * 翻译系列配置
   * @param {Object} series - 系列配置
   * @returns {Object} 翻译后的系列配置
   * @private
   */
  _translateSeries(series) {
    const translatedSeries = { ...series };

    // 翻译系列名称
    if (series.nameKey) {
      translatedSeries.name = this.t(series.nameKey);
    }

    return translatedSeries;
  }

  /**
   * 获取翻译后的图表配置（包含图例和提示框翻译）
   * @param {Object} baseOption - 基础图表配置
   * @returns {Object} 翻译后的图表配置
   */
  getTranslatedChartOptionWithLegendAndTooltip(baseOption) {
    // 先获取基础翻译配置
    const translatedOption = this.getTranslatedChartOption(baseOption);

    // 翻译图例
    if (translatedOption.legend) {
      translatedOption.legend = this._translateLegend(translatedOption.legend);
    }

    // 翻译提示框
    if (translatedOption.tooltip) {
      translatedOption.tooltip = this._translateTooltip(translatedOption.tooltip);
    }

    return translatedOption;
  }

  /**
   * 翻译图例配置
   * @param {Object|Array} legend - 图例配置
   * @returns {Object|Array} 翻译后的图例配置
   * @private
   */
  _translateLegend(legend) {
    if (Array.isArray(legend)) {
      return legend.map(l => this._translateSingleLegend(l));
    }
    return this._translateSingleLegend(legend);
  }

  /**
   * 翻译单个图例
   * @param {Object} legend - 单个图例配置
   * @returns {Object} 翻译后的图例配置
   * @private
   */
  _translateSingleLegend(legend) {
    const translatedLegend = { ...legend };

    // 翻译图例数据
    if (legend.data && Array.isArray(legend.data)) {
      translatedLegend.data = legend.data.map(item => {
        if (typeof item === 'object') {
          const translatedItem = { ...item };
          if (item.nameKey) {
            translatedItem.name = this.t(item.nameKey);
          }
          return translatedItem;
        } else if (typeof item === 'string' && item.startsWith('charts.')) {
          // 如果数据项本身就是翻译键
          return this.t(item);
        }
        return item;
      });
    }

    return translatedLegend;
  }

  /**
   * 翻译提示框配置
   * @param {Object} tooltip - 提示框配置
   * @returns {Object} 翻译后的提示框配置
   * @private
   */
  _translateTooltip(tooltip) {
    const translatedTooltip = { ...tooltip };

    // 如果有自定义的formatter函数，需要特殊处理
    if (tooltip.formatterKey) {
      translatedTooltip.formatter = this._createTranslatedFormatter(tooltip.formatterKey);
    }

    return translatedTooltip;
  }

  /**
   * 创建翻译后的提示框格式化函数
   * @param {string} formatterKey - 格式化函数的翻译键前缀
   * @returns {Function} 翻译后的格式化函数
   * @private
   */
  _createTranslatedFormatter(formatterKey) {
    return (params) => {
      // 这里可以根据具体需求实现提示框内容的翻译
      // 例如翻译系列名称、标签等
      if (Array.isArray(params)) {
        return params.map(param => {
          const translatedParam = { ...param };
          // 翻译系列名称
          if (param.seriesName) {
            const seriesNameKey = `${formatterKey}.${param.seriesName}`;
            translatedParam.seriesName = this.t(seriesNameKey, param.seriesName);
          }
          return translatedParam;
        });
      }
      return params;
    };
  }

  /**
   * 获取常用图表文本翻译
   * @param {string} textType - 文本类型
   * @returns {string} 翻译后的文本
   */
  getChartText(textType) {
    const textMap = {
      // 图例文本
      'actualValue': 'charts.elements.actualValue',
      'standardValue': 'charts.elements.standardValue',
      'normalValue': 'charts.elements.normalValue',
      'excellentIndicator': 'charts.elements.excellentIndicator',
      'needAttention': 'charts.elements.needAttention',
      'imbalance': 'charts.elements.imbalance',
      'insufficient': 'charts.elements.insufficient',
      'moderate': 'charts.elements.moderate',
      'good': 'charts.elements.good',
      'excellent': 'charts.elements.excellent',
      
      // 提示框文本
      'standard': 'charts.elements.standard',
      'measured': 'charts.elements.measured',
      'comparison': 'charts.elements.comparison',
      'trend': 'charts.elements.trend',
      'percentage': 'charts.elements.percentage',
      'score': 'charts.elements.score',
      'level': 'charts.elements.level'
    };

    const key = textMap[textType];
    return key ? this.t(key) : textType;
  }

  /**
   * 获取翻译后的图例数据
   * @param {Array} legendData - 原始图例数据
   * @returns {Array} 翻译后的图例数据
   */
  getTranslatedLegendData(legendData) {
    if (!Array.isArray(legendData)) {
      return legendData;
    }

    return legendData.map(item => {
      if (typeof item === 'string') {
        return this.getChartText(item);
      } else if (typeof item === 'object' && item.name) {
        return {
          ...item,
          name: this.getChartText(item.name)
        };
      }
      return item;
    });
  }

  /**
   * 创建标准的提示框格式化函数
   * @param {Object} options - 配置选项
   * @returns {Function} 格式化函数
   */
  createTooltipFormatter(options = {}) {
    const {
      showSeriesName = true,
      showValue = true,
      showPercentage = false,
      customLabels = {}
    } = options;

    return (params) => {
      if (!Array.isArray(params)) {
        params = [params];
      }

      let html = '';
      
      // 添加标题（通常是x轴的值）
      if (params.length > 0 && params[0].name) {
        html += `<div style="font-size:16px;color:#2C324E;font-weight:500;">
                   ${params[0].name}
                 </div>`;
      }

      // 添加每个系列的信息
      params.forEach(param => {
        html += '<div style="margin-top:10px;">';
        
        if (showSeriesName && param.seriesName) {
          const translatedSeriesName = customLabels[param.seriesName] || 
                                     this.getChartText(param.seriesName) || 
                                     param.seriesName;
          html += `<span style="font-size:14px;color:#64749C;">${translatedSeriesName}：</span>`;
        }
        
        if (showValue) {
          html += `<span style="float:right;font-size:14px;color:#003ec6;font-weight:bold;">
                     ${param.value}${showPercentage ? '%' : ''}
                   </span>`;
        }
        
        html += '</div>';
      });

      return html;
    };
  }
}

export default ChartI18nHelper;